import { ISchedule, IUserFitSimple } from "@gettactic/api";
import { IconValue } from "../../../components";
import { Furniture } from "../furniture-list";

export type ShapeTextAlignment = "left" | "center" | "right";

export type ShapeType =
  | "rectangle"
  | "circle"
  | "text"
  | "svg"
  | "icon"
  | "group"
  | "dynamic-furniture"
  | "furniture"
  | "spot"
  | "image"
  | "zone"
  | "wall"
  | "wall-path";

export type SpotShapeShape = "default" | "custom";

export const SHAPE_TYPE: Record<Uppercase<ShapeType>, ShapeType> = {
  RECTANGLE: "rectangle",
  CIRCLE: "circle",
  TEXT: "text",
  SVG: "svg",
  ICON: "icon",
  GROUP: "group",
  FURNITURE: "furniture",
  "DYNAMIC-FURNITURE": "dynamic-furniture",
  SPOT: "spot",
  IMAGE: "image",
  ZONE: "zone",
  WALL: "wall",
  "WALL-PATH": "wall-path"
} as const;

export type LayerType = "background" | "floor" | "shapes" | "spots" | "labels";
export type DrawShape = "rectangle" | "circle" | "star" | "zone";
export type SpotType =
  | "workspace"
  | "desk"
  | "meeting_room"
  | "parking_lot"
  | "parking_space";

export enum SpotShapeType {
  Node = "workspace",
  Desk = "desk",
  Room = "meeting_room",
  Parking = "parking_lot",
  ParkingSpace = "parking_space"
}

export const SpotShapeTypeToLabel: Record<SpotShapeType, string> = {
  workspace: "Workspace",
  desk: "Desk",
  meeting_room: "Meeting Room",
  parking_lot: "Parking Lot",
  parking_space: "Parking Space"
};

export type SpotAppearance = SpotShapeType | "circle";

export const SpotAppearanceToLabel: Record<SpotAppearance, string> = {
  workspace: "Workspace",
  desk: "Desk",
  meeting_room: "Meeting Room",
  parking_lot: "Parking Lot",
  parking_space: "Parking Space",
  circle: "Circle"
};

export interface ShapeTextOptions {
  fontSize?: number;
  fontFamily?: string;
  isBold?: boolean;
  isUnderline?: boolean;
  isItalic?: boolean;
  textColor?: string;
  textAlign?: ShapeTextAlignment;
}

export interface BaseShape {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  cornerRadius?: number;
  color?: string;
  noFill?: boolean;
  stroke?: string;
  strokeWidth?: number;
  hidden?: boolean;
  locked?: boolean;
  onClick?: () => void;
  layerType: LayerType;
  rotation?: number;
  layerLabel?: string;
  scaleX?: number;
  scaleY?: number;
}

export interface RectangleShape extends BaseShape {
  type: "rectangle";
}

export interface CircleShape extends BaseShape {
  type: "circle";
  radius: number;
}

export interface TextShape extends BaseShape, ShapeTextOptions {
  type: "text";
  text: string;
}

export interface SVGShape extends BaseShape {
  type: "svg";
  path: string;
  svgContent?: string;
  stroke: string;
  strokeWidth: number;
  scaleX?: number;
  scaleY?: number;
}

export interface ImageShape extends BaseShape {
  type: "image";
  path: string;
}

export interface IconShape extends BaseShape, ShapeTextOptions {
  type: "icon";
  icon: IconValue;
  /** Optional map category (same registry zones use — `zoneTypes` on the
   *  map value). Categorized icons appear in the viewer legend as points
   *  of interest (e.g. Restrooms, Fire Exits) with a per-map count, and
   *  hovering the legend entry highlights them on the map. */
  categoryTypeId?: string;
  /** Makes the icon clickable in the viewer, navigating to another
   *  office's map or an external URL — same mechanism as ZoneLink. Not
   *  shown in the legend; only surfaces as a hover tooltip on the icon. */
  link?: ZoneLink;
  /** CDN URL for API-sourced icons — when present, fetched like furniture instead of via IconFromSet */
  iconUrl?: string;
  /** Pre-trimmed SVG markup. When set, renderers use this directly instead
   *  of re-rendering IconFromSet — lets us bake a viewBox that hugs the
   *  actual content bbox at placement time so visible padding inside the
   *  shape goes away. */
  svgContent?: string;
  scaleX?: number;
  scaleY?: number;
  text?: string;
}

export interface DynamicTableLayout {
  version: 1;
  shape: "round" | "rectangle";
  tableWidth: number;
  tableHeight: number;
  chairs: number;
  top: number;
  bottom: number;
  left: number;
  right: number;
  chairStyle: "standard" | "large";
  chairScale: number;
  gap: number;
  cornerRadius: number;
  startAngle: number;
}
export interface DynamicFurnitureShape extends BaseShape {
  type: "dynamic-furniture";
  layout: DynamicTableLayout;
}

export interface FurnitureShape extends BaseShape, ShapeTextOptions {
  type: "furniture";
  furniture: Furniture;
  scaleX?: number;
  scaleY?: number;
  text?: string;
}

export interface GroupShape extends BaseShape {
  type: "group";
  children: Shape[];
}

export type ZoneType = "room" | "open_area" | "corridor" | "clickable";

/** Navigation target for a zone: clicking it in the viewer switches to
 *  another office's map or opens an external URL. */
export interface ZoneLink {
  type: "office" | "url";
  officeId?: string;
  /** Floor to land on within the linked office (the /map view is
   *  office + floor scoped). Falls back to the office default floor. */
  floorId?: string;
  url?: string;
  /** Display label captured at edit time (e.g. the office name) — used
   *  for tooltips without needing a lookup in the viewer. */
  label?: string;
  /** Optional longer description. When set (icons only), clicking shows a
   *  popover with this text and a button to follow the link, instead of
   *  navigating immediately. */
  description?: string;
}

export type ZoneLabelPosition =
  | "top-left"
  | "top-center"
  | "center"
  | "bottom-left"
  | "bottom-center";

export interface ZoneShape extends BaseShape {
  type: "zone";
  zoneType: ZoneType;
  name: string;
  /** Where the name renders within the zone bounds. Default 'top-left'. */
  labelPosition?: ZoneLabelPosition;
  /** Font size (px) of the rendered name. Default 14. */
  labelFontSize?: number;
  borderColor?: string;
  borderWidth?: number;
  fillOpacity?: number;
  containedSpotIds?: string[];
  /** User-defined category ID — resolves to a `ZoneTypeDefinition` on the
   *  map value. When set, viewers paint the zone with the category's
   *  color and the legend uses it to group/filter zones. */
  categoryTypeId?: string;
  /** Makes the zone clickable in the viewer, navigating to another
   *  office's map or an external URL (e.g. campus map → office map). */
  link?: ZoneLink;
  /** Polygon outline relative to the zone origin (x/y stays the bbox
   *  top-left). With 3+ vertices the zone renders as a closed path
   *  instead of the width/height rectangle. */
  vertices?: { x: number; y: number }[];
  /** edges[i] connects vertices[i] → vertices[i+1] (wrapping back to the
   *  first vertex). Curvature works like wall edges: perpendicular peak
   *  displacement of a quadratic bezier. */
  edges?: { curvature?: number; curvatureCenter?: number }[];
  /** Organisation zone this region is tagged with. Stamped by the server
   *  on save (also for legacy `categoryTypeId` categories, which it
   *  promotes to zones); the builder sets it when picking a zone. */
  zone_id?: string;
  /** Area in square canvas pixels, written by `prepareRegionsForSave` so
   *  the server can derive m² without redoing the polygon maths. */
  areaPx?: number;
  /** Zone shape this one is drawn inside (the smallest region whose
   *  outline contains this region's corners), written on save. */
  parentShapeId?: string;
}

/** Map-level category registry. Lives on `MapCanvasValue` so it persists
 *  in the saved JSON alongside `compositeTemplates`. Icon categories
 *  (points of interest) are map-local; entries whose id is an organisation
 *  zone id are a cache the server refreshes on save and on zone edits. */
export interface ZoneTypeDefinition {
  id: string;
  name: string;
  /** Fill color used when a zone references this type. Hex / CSS color. */
  color: string;
  /** Icon-set key; present on entries the server caches for organisation zones. */
  icon?: string;
}

/** One zone category row, as bridged out for an external (native) legend
 *  — see `ViewSVGProps.onLegendDataChange`. `isVisible` mirrors the
 *  viewer's own toggle state, which starts every category hidden until
 *  the legend opts it in. */
export interface LegendZoneCategoryData {
  id: string;
  name: string;
  color: string;
  isVisible: boolean;
  desks: number;
  meetingRooms: number;
  /** Real-world area — only present for admins on a calibrated map, same
   *  admin gate as the in-webview legend. */
  areaSqm: number | null;
}

/** One points-of-interest row — a category of icons (fire extinguishers,
 *  restrooms, etc.), informational only, no toggle. */
export interface LegendPointOfInterestData {
  id: string;
  name: string;
  count: number;
  iconUrl?: string | null;
}

/** One zone with a navigation link, listed so it's reachable without
 *  hunting for it on the map. An external legend triggers it via
 *  `ViewSVGLegendHandle.triggerLinkedZone(id)` rather than needing the
 *  link's own office/URL details — ViewSVG already knows how to resolve
 *  and follow it (`onZoneLinkClick`). */
export interface LegendLinkedZoneData {
  id: string;
  name: string;
  color?: string | null;
}

/** Bridged snapshot of the interactive zone legend's content — fired via
 *  `ViewSVGProps.onLegendDataChange` whenever the underlying map data or
 *  the viewer's own visibility/hover state changes. */
export interface LegendBridgeData {
  zoneCategories: LegendZoneCategoryData[];
  pointsOfInterest: LegendPointOfInterestData[];
  linkedZones: LegendLinkedZoneData[];
  totalDesks: number;
  totalMeetingRooms: number;
  totalAreaSqm: number | null;
}

/** Lets a host imperatively drive the legend's own state — the same
 *  toggle/hover/show-all actions the in-webview ZoneLegend triggers on
 *  itself, exposed here for a bridged external panel to call. */
export interface ViewSVGLegendHandle {
  toggleZoneCategory: (categoryId: string) => void;
  setVisibleZoneCategories: (categoryIds: string[]) => void;
  setZoneCategoryHover: (categoryId: string | null) => void;
  /** Highlights points-of-interest icons of this category on the map —
   *  the touch/tap equivalent of hovering a "Points of Interest" legend
   *  row on web. */
  setIconCategoryHover: (categoryId: string | null) => void;
  triggerLinkedZone: (zoneId: string) => void;
}

export interface WallDoor {
  id: string;
  /** Position along the wall as a ratio (0 = start, 1 = end) */
  position: number;
  /** Door width in pixels along the wall */
  width: number;
  /** When true, the door line is not rendered (gap in wall remains) */
  transparent?: boolean;
}

export interface WallWindow {
  id: string;
  /** Position along the wall as a ratio (0 = start, 1 = end) */
  position: number;
  /** Window width in pixels along the wall */
  width: number;
}

export type WallDashPattern = "solid" | "dashed" | "dotted";
export type WallLineCap = "butt" | "round";

export interface WallShape extends BaseShape {
  type: "wall";
  x2: number;
  y2: number;
  thickness: number;
  doors?: WallDoor[];
  windows?: WallWindow[];
  /** Perpendicular offset (px) from the wall for quadratic bezier curve. 0 = straight. */
  curvature?: number;
  /** Position along the wall (0–1) where the curve peaks. Defaults to 0.5 (midpoint). */
  curvatureCenter?: number;
  /** Line dash pattern. Defaults to 'solid'. */
  dashPattern?: WallDashPattern;
  /** Line cap at the start endpoint. Defaults to 'butt'. */
  lineCapStart?: WallLineCap;
  /** Line cap at the end endpoint. Defaults to 'butt'. */
  lineCapEnd?: WallLineCap;
  /** Skip this wall when detecting enclosed rooms to flood-fill white (see
   *  `computeWallFloorPolygons`). Set on the grid tool's painted lines —
   *  a dense grid of thin dividers isn't a room and shouldn't paint over
   *  whatever's underneath it. */
  excludeFromFloorFill?: boolean;
}

export interface WallPathVertex {
  x: number;
  y: number;
  /** Corner radius (px) at this vertex. 0 = sharp corner. */
  cornerRadius?: number;
}

export interface WallPathEdge {
  /** Perpendicular offset (px) for quadratic bezier curve. 0 = straight. */
  curvature?: number;
  /** Position along the edge (0–1) where the curve peaks. Defaults to 0.5. */
  curvatureCenter?: number;
  doors?: WallDoor[];
  windows?: WallWindow[];
}

export interface WallPathShape extends BaseShape {
  type: "wall-path";
  /** Ordered vertices forming the path. Minimum 2. */
  vertices: WallPathVertex[];
  /** Edge data. edges[i] connects vertices[i] to vertices[i+1]. Length = closed ? vertices.length : vertices.length - 1 */
  edges: WallPathEdge[];
  thickness: number;
  /** If true, last vertex connects back to first vertex */
  closed?: boolean;
  /** Line dash pattern. Defaults to 'solid'. */
  dashPattern?: WallDashPattern;
  /** Line cap at the start endpoint (first vertex). Defaults to 'butt'. */
  lineCapStart?: WallLineCap;
  /** Line cap at the end endpoint (last vertex). Defaults to 'butt'. */
  lineCapEnd?: WallLineCap;
  /** Fill color for the interior of a closed wall-path. Ignored when
   *  `closed` is false. Undefined / empty string = no fill (default). */
  fillColor?: string;
  /** Fill opacity 0..1. Defaults to 1 when fillColor is set. */
  fillOpacity?: number;
  /** Skip this wall when detecting enclosed rooms to flood-fill white (see
   *  `computeWallFloorPolygons`). Set on the grid tool's painted lines —
   *  a dense grid of thin dividers isn't a room and shouldn't paint over
   *  whatever's underneath it. */
  excludeFromFloorFill?: boolean;
}

export type SpotColorSource = "manual" | "group" | "team" | "default";

export interface CompositeTemplate {
  id: string;
  name: string;
  /** Shapes in the template, with positions relative to the anchor spot */
  shapes: Shape[];
  /** Index of the anchor spot within shapes[] */
  anchorIndex: number;
  /** Pre-computed bounding box for spacing calculations */
  bounds: { width: number; height: number };
}

export type SpotOrientation = "top" | "bottom" | "left" | "right";
export type SpotLabelPlacement =
  | "on-rect"
  | "on-node"
  | "outside-node"
  | "outside"
  | "hidden"
  | "custom";
export type SpotLabelOrientation = "auto" | "top" | "bottom";

/** Availability-state highlight area for a spot, rendered in the viewer
 *  with the same color the state dot uses (e.g. tint the whole meeting
 *  room when it's occupied). Coordinates are relative to the spot origin. */
export interface SpotStateArea {
  /** Keep the indicator at its spot anchor (e.g. the conference table).
   * Unset preserves automatic centering on the highlight area. */
  indicatorAnchor?: "spot" | "area";
  /** 'spot-rect' follows the spot's own rect (rooms/desks); 'rect' is a
   *  custom rectangle; 'polygon' is drawn point-by-point on the map.
   *  All coordinates are relative to the spot origin. */
  kind: "spot-rect" | "rect" | "polygon";
  /** Hide the state dot in the viewer — the area itself (plus any state
   *  icon) is the availability indicator */
  hideDot?: boolean;
  /** Custom tint for the occupied/booked state (hex, e.g. "#ef4444").
   *  Unset falls back to the same computed color the dot uses. */
  occupiedColor?: string;
  rect?: {
    x: number;
    y: number;
    width: number;
    height: number;
    cornerRadius?: number;
  };
  /** Polygon vertices (kind 'polygon'), relative to the spot origin */
  points?: { x: number; y: number }[];
}

/** Per-availability-state icons that replace the state dot in the viewer
 *  (e.g. a car when a parking spot is occupied). `offsetX`/`offsetY`/`size`
 *  position and size whichever icon is showing — shared between the two
 *  states (rather than per-icon) so the icon doesn't jump around as
 *  availability changes. Unset defaults to the legacy behavior: centered on
 *  the dot, sized to match it (`circleRadius * 2`). */
export interface SpotStateIcons {
  occupied?: IconValue;
  available?: IconValue;
  /** Horizontal offset from the dot center, in spot-local units. */
  offsetX?: number;
  /** Vertical offset from the dot center, in spot-local units. */
  offsetY?: number;
  /** Icon width/height in pixels. Defaults to `circleRadius * 2`. */
  size?: number;
  /** Custom color for whichever icon is showing. Unset falls back to the
   *  same computed color the dot uses. */
  color?: string;
  /** Colored backdrop behind the icon specifically for the occupied state
   *  (e.g. a red disc behind a car icon) — the dot itself is otherwise
   *  fully hidden once a state icon is configured. Unset keeps the legacy
   *  behavior: no backdrop, just the icon. */
  occupiedBackgroundColor?: string;
}

export interface SpotShapeItem {
  id: string;
  spotAppearance?: SpotAppearance;
  spotShape?: SpotShapeShape;
  isInactive?: boolean;
  spotType: SpotShapeType;
  orientation: SpotOrientation;
  labelPlacement: SpotLabelPlacement;
  labelOrientation?: SpotLabelOrientation;
  textAlign: ShapeTextAlignment;
  spacing?: number;
  rowSpacing?: number;
  name?: string;
  colorSource?: SpotColorSource;
  rectProps?: {
    cornerRadius?: number;
    opacity?: number;
    color?: string;
  };
  circleProps?: {
    radius?: number;
    color?: string;
    stroke?: string;
    strokeWidth?: number;
  };
  textProps?: {
    text?: string;
    color?: string;
    fontSize?: number;
    fontFamily?: string;
    fontStyle?: string;
    rotation?: number;
    marginTop?: number;
    marginLeft?: number;
    width?: number;
  };
  resource_id?: string;
  /** Availability-state highlight area (viewer only) */
  stateArea?: SpotStateArea;
  /** Availability-state icons replacing the state dot (viewer only) */
  stateIcons?: SpotStateIcons;
  /** Companion shapes rendered relative to this spot (positions relative to spot x,y) */
  companions?: Shape[];
  /** Links this spot to a composite template definition */
  compositeTemplateId?: string;
}

export interface SpotRowProps {
  id: string;
  items: SpotShapeItem[];
}

export interface SpotShape
  extends BaseShape,
    SpotShapeItem,
    Omit<ShapeTextOptions, "textAlign"> {
  type: "spot";
  rowsExtraSpacing: number;
  label: string;
  name?: string;
}
export interface SpotShape001 extends BaseShape, ShapeTextOptions {
  type: "spot";
  orientation: SpotOrientation;
  labelPlacement: SpotLabelPlacement;
  spotType: SpotType;
  rows: SpotRowProps[];
  spacing: number;
  rowSpacing: number;
  rowsExtraSpacing: number;
}

export type Shape =
  | RectangleShape
  | TextShape
  | GroupShape
  | SVGShape
  | CircleShape
  | SpotShape
  | IconShape
  | FurnitureShape
  | DynamicFurnitureShape
  | ImageShape
  | ZoneShape
  | WallShape
  | WallPathShape;

export interface DrawToolState {
  active: boolean;
  selectedShape: DrawShape;
  startPoint: { x: number; y: number } | null;
}

export interface MapCurrentPosition {
  scaleX: number;
  scaleY: number;
  x: number;
  y: number;
}

export interface MapInteractiveZoneActionOptions {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Cmd (Mac) / Ctrl click — opens the reservation directly */
  isCmdOrCtrl?: boolean;
}

export interface MapInteractiveZone {
  zoneId: string;
  notAssigned: boolean;
  requiresApproval: boolean;
  isWalkUp?: boolean;
  disabled: boolean;
  /** Walk-up-corrected occupancy: for a walk-up, only a still-open (no-end)
   *  check-in counts, ignoring `disabled` (that reflects the whole queried
   *  window, not "right now"). For every other resource type this is the
   *  same as `disabled`. Renderers that paint an occupied/available state
   *  must read this instead of `disabled` directly. */
  isOccupied: boolean;
  reservedSlots: number;
  hasSchedules: boolean;
  actualSchedule?: ISchedule | null;
  scheduledUserId?: string;
  showCircleProfilePhoto: boolean;
  assignedUser?: IUserFitSimple | null;
  /** Resource is dedicated to the assigned user — the avatar renders at
   *  full opacity (vs the faded restricted-assignment look) and nobody
   *  else can book it. */
  isDedicated?: boolean;
  showPulse: boolean;
  isLastUsed?: boolean;
  isSelected?: boolean;
  /** 1-based position of the spot within a multi-resource selection (e.g. the rooms
   *  of one reservation). Renders a numbered badge; omit for a single selection. */
  selectionPosition?: number;
  /** Spot excluded by the current picking mode (e.g. a desk while the map is armed to
   *  add a room): rendered faded and inert — no hover, no click. */
  isNotSelectable?: boolean;
  lastAccessedDeskResourceId?: string | null;
  lastAccessedRoomResourceId?: string | null;
  onAction?: (options: MapInteractiveZoneActionOptions) => void;
}

export type MapInteractiveZones = Record<string, MapInteractiveZone>;

export interface ResourceEditingOptions {
  isOpen: boolean;
  resourceIds: string[];
}
